import { apiAxios, HostDaySlot, HostSlotsResponseResponseWrapper } from "api"
import { useEffect, useState } from "react"

export function useHostSlots(hostResourceId: number | undefined, dates: string[]) {
  const [availability, setAvailability] = useState<HostDaySlot[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  const datesKey = dates.join(",")

  useEffect(() => {
    if (!hostResourceId || dates.length === 0) {
      setAvailability([])
      return
    }

    let cancelled = false
    setLoading(true)
    setError(false)

    // Use custom paramsSerializer to send dates=X&dates=Y instead of dates[]=X
    // (the backend proxy doesn't forward bracket-notation array params)
    apiAxios
      .get<HostSlotsResponseResponseWrapper>(`/web/host/resources/slots`, {
        params: { room_id: hostResourceId, dates },
        paramsSerializer: (params) => {
          const parts: string[] = []
          for (const [key, val] of Object.entries(params as Record<string, any>)) {
            if (Array.isArray(val)) {
              val.forEach((v) => parts.push(`${key}=${encodeURIComponent(v)}`))
            } else {
              parts.push(`${key}=${encodeURIComponent(val)}`)
            }
          }
          return parts.join("&")
        },
      })
      .then((res) => {
        if (cancelled) return
        if (res.data?.errorCode) {
          setError(true)
        } else if (res.data?.body?.availability) {
          setAvailability(res.data.body.availability)
        }
      })
      .catch(() => {
        if (!cancelled) setError(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostResourceId, datesKey])

  return { availability, loading, error }
}

/**
 * Given a date's availability, returns which time slots are free/busy.
 */
export function getSlotStatus(
  dateAvailability: HostDaySlot | undefined,
  timeSlots: string[],
): { free: string[]; busy: string[] } {
  if (!dateAvailability) {
    return { free: timeSlots, busy: [] }
  }

  const busy = new Set<string>()

  for (const slot of dateAvailability.busySlots) {
    for (const ts of timeSlots) {
      if (ts >= slot.from && ts < slot.to) {
        busy.add(ts)
      }
    }
  }

  return {
    free: timeSlots.filter((ts) => !busy.has(ts)),
    busy: Array.from(busy),
  }
}
